import type { ProviderPatterns } from './thirdPartyPromptTypes.ts';

// `regexp` is compiled once, when the table is built. `i` for RFC 3986 host case.
type CompiledPattern = { source: string; regexp: RegExp };

const host = (pattern: string): CompiledPattern => {
	const source = `^https:\\/\\/${pattern}(?:[/?#]|$)`;
	return { source, regexp: new RegExp(source, 'i') };
};

const hostPath = (pattern: string, path: string): CompiledPattern => {
	const source = `^https:\\/\\/${pattern}${path}`;
	return { source, regexp: new RegExp(source, 'i') };
};

/** Every key must be a key of `smartlinkExtensionKeyToAppIdMap`, or it reaches no product. CCINT-8754 */
const PROVIDER_PATTERNS: readonly { key: string; patterns: CompiledPattern[] }[] = [
	{ key: 'adobe-xd-object-provider', patterns: [host('xd\\.adobe\\.com')] },
	{ key: 'airtable-object-provider', patterns: [host('airtable\\.com')] },
	{
		key: 'amplitude-object-provider',
		patterns: [host('app\\.amplitude\\.com'), host('analytics\\.amplitude\\.com')],
	},
	{ key: 'asana-object-provider', patterns: [host('app\\.asana\\.com')] },
	{
		key: 'azuredevops-object-provider',
		patterns: [host('dev\\.azure\\.com'), host('[^/]+\\.visualstudio\\.com')],
	},
	{ key: 'box-object-provider', patterns: [host('[^/]+\\.box\\.com')] },
	{ key: 'clickup-object-provider', patterns: [host('app\\.clickup\\.com')] },
	{ key: 'docusign-object-provider', patterns: [host('apps\\.docusign\\.com')] },
	{ key: 'dovetail-object-provider', patterns: [host('[^/]+\\.dovetail(?:app)?\\.com')] },
	{ key: 'dropbox-object-provider', patterns: [host('(?:www\\.)?dropbox\\.com')] },
	{ key: 'figma-object-provider', patterns: [host('(?:[\\w.-]+\\.)?figma\\.com')] },
	{
		key: 'github-object-provider',
		patterns: [host('gist\\.github\\.com'), host('github\\.com')],
	},
	{ key: 'gitlab-object-provider', patterns: [host('gitlab\\.com')] },
	{
		key: 'google-object-provider',
		patterns: [host('docs\\.google\\.com'), host('drive\\.google\\.com')],
	},
	{ key: 'hubspot-object-provider', patterns: [host('[^/]+\\.hubspot\\.com')] },
	{ key: 'launchdarkly-object-provider', patterns: [host('app\\.launchdarkly\\.com')] },
	{ key: 'lucid-object-provider', patterns: [host('lucid\\.app')] },
	{ key: 'miro-object-provider', patterns: [host('miro\\.com')] },
	{ key: 'monday-object-provider', patterns: [host('(?:[^/]+\\.)?monday\\.com')] },
	{ key: 'ms-power-bi-object-provider', patterns: [host('app\\.powerbi\\.com')] },
	{
		key: 'ms-teams-object-provider',
		patterns: [
			host('teams\\.cloud\\.microsoft'),
			// microsoft.com serves more than Teams, so match only the deep-link paths.
			hostPath('[^/]+\\.microsoft\\.com', '\\/l\\/(?:channel|chat|message)\\/'),
		],
	},
	{ key: 'mural-object-provider', patterns: [host('app\\.mural\\.co')] },
	{ key: 'notion-object-provider', patterns: [host('(?:www\\.)?notion\\.so')] },
	{
		key: 'onedrive-object-provider',
		patterns: [
			host('[^/]+\\.sharepoint\\.com'),
			host('onedrive\\.live\\.com'),
			host('1drv\\.ms'),
			// A customer vanity SharePoint domain ORS ships; not a typo.
			host('team\\.amat\\.com'),
		],
	},
	{ key: 'pagerduty-object-provider', patterns: [host('[^/]+\\.pagerduty\\.com')] },
	{ key: 'pipedrive-object-provider', patterns: [host('[^/]+\\.pipedrive\\.com')] },
	// Exclude sandboxes: they are a separate provider with no app mapping.
	{
		key: 'salesforce-object-provider',
		patterns: [host('(?![^/]+\\.sandbox)[^/]+\\.lightning\\.force\\.com')],
	},
	{
		key: 'sentry-object-provider',
		patterns: [host('[^/]+\\.sentry\\.io'), host('sentry\\.io')],
	},
	{
		key: 'slack-object-provider',
		patterns: [host('[^/]+\\.slack\\.com'), host('slack-files\\.com')],
	},
	// `\w{2,}`: Smartsheet serves several TLDs, e.g. `.com`, `.eu`.
	{ key: 'smartsheet-object-provider', patterns: [host('app\\.smartsheet\\.\\w{2,}')] },
	{ key: 'stripe-object-provider', patterns: [host('dashboard\\.stripe\\.com')] },
	{ key: 'todoist-object-provider', patterns: [host('app\\.todoist\\.com')] },
	{ key: 'webex-object-provider', patterns: [host('[^/]+\\.webex\\.com')] },
	{
		key: 'zeplin-object-provider',
		patterns: [host('app\\.zeplin\\.io'), host('zpl\\.io')],
	},
	{ key: 'zendesk-object-provider', patterns: [host('[^/]+\\.zendesk\\.com')] },
];

/** Raw table for tests and debugging. Consumers call `matchProviderKey`. */
export const getProviderPatterns = (): readonly ProviderPatterns[] => PROVIDER_PATTERNS;

/** Provider key for an address, or `undefined`. First match wins. */
export const matchProviderKey = (url: string): string | undefined =>
	PROVIDER_PATTERNS.find(({ patterns }) => patterns.some(({ regexp }) => regexp.test(url)))?.key;
